import { ProblemError, TransportError, ValidationError } from './errors.ts'
import type { FieldProblem, ProblemDocument, ProblemParams } from './types.ts'

/**
 * Turning a failed response into an error object.
 *
 * Everything here is defensive by design: the body may be a proper problem document, an
 * HTML error page from CloudFront, or empty. Only the first case is our contract, and the
 * other two must not produce a crash on the way to reporting the real failure.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Params as sent, or null. Absent and null are both "no params" — the backend omits the
 * member (`exclude_none`), but a null must not become `{}` either, because a caller
 * checking `params === null` is asking a real question.
 */
function readParams(value: unknown): ProblemParams | null {
  return isRecord(value) ? (value as ProblemParams) : null
}

function readFieldProblems(value: unknown): readonly FieldProblem[] | null {
  if (!Array.isArray(value)) return null
  const fields: FieldProblem[] = []
  for (const entry of value) {
    if (!isRecord(entry)) continue
    // `field` and `code` are required by the schema; an entry without them is not one
    // of ours and is skipped rather than surfaced as `undefined` in a form binding.
    if (typeof entry.field !== 'string' || typeof entry.code !== 'string') continue
    fields.push({
      field: entry.field,
      code: entry.code,
      params: readParams(entry.params),
      message: typeof entry.message === 'string' ? entry.message : null,
    })
  }
  return fields
}

/** Reads a problem document out of an arbitrary parsed body, keeping only known shapes. */
export function toProblemDocument(body: unknown, status: number): ProblemDocument {
  if (!isRecord(body)) return { status }
  return {
    type: typeof body.type === 'string' ? body.type : undefined,
    title: typeof body.title === 'string' ? body.title : undefined,
    status: typeof body.status === 'number' ? body.status : status,
    detail: typeof body.detail === 'string' ? body.detail : undefined,
    instance: typeof body.instance === 'string' ? body.instance : undefined,
    // Not validated against the union: an unknown code means the backend added one and
    // the frontend has not caught up. Passing it through lets the message layer fall
    // back, where dropping it would lose the only clue about what happened.
    code: typeof body.code === 'string' ? (body.code as ProblemDocument['code']) : undefined,
    params: readParams(body.params),
    request_id: typeof body.request_id === 'string' ? body.request_id : null,
    errors: readFieldProblems(body.errors),
  }
}

/**
 * Builds the error for a failed response.
 *
 * A 422 becomes a `ValidationError` — the subclass callers bind to form fields — and
 * everything else a `ProblemError`.
 */
export async function toApiError(response: Response): Promise<ProblemError> {
  // The header is the more reliable source: on a 500 the body is built above the
  // logging middleware, so the two can disagree.
  const headerRequestId = response.headers.get('X-Request-ID')

  let body: unknown = null
  try {
    const text = await response.text()
    body = text ? JSON.parse(text) : null
  } catch {
    // An HTML error page or a truncated body. The status is still the real information.
    body = null
  }

  const document = toProblemDocument(body, response.status)
  const requestId = headerRequestId ?? document.request_id ?? null

  return response.status === 422
    ? new ValidationError(response.status, document, requestId)
    : new ProblemError(response.status, document, requestId)
}

/**
 * Normalises whatever `fetch` throws.
 *
 * An abort is separated from a network failure because it is usually deliberate — a
 * cancelled query on unmount — and should not be reported as an outage.
 */
export function toTransportError(cause: unknown): TransportError {
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return new TransportError('aborted', 'The request was aborted', { cause })
  }
  const detail = cause instanceof Error ? cause.message : String(cause)
  return new TransportError('network', `The request could not be sent: ${detail}`, { cause })
}
